import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getReportDrillDown } from '../../api';

function formatCurrency(value) {
  if (value == null) return '$0';
  return '$' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function DrillDownModal({ isOpen, onClose, reportType, context, dateRange, filters }) {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && context) {
      fetchData(1);
    }
  }, [isOpen, context]);

  const fetchData = async (page) => {
    setLoading(true);
    try {
      const params = {
        reportType,
        page,
        limit: 20,
        dateRange: dateRange.preset,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...filters,
      };
      if (context.stageName) params.stageName = context.stageName;
      if (context.period) params.period = context.period;
      if (context.groupLabel) params.groupLabel = context.groupLabel;
      // Matrix drill-down params
      if (context.dataSource) params.dataSource = context.dataSource;
      if (context.rowDimensions) params.rowDimensions = context.rowDimensions.join(',');
      if (context.rowValues) params.rowValues = context.rowValues.join(',');
      if (context.colDimension) params.colDimension = context.colDimension;
      if (context.colValue) params.colValue = context.colValue;
      // Backward compat
      if (context.rowDimension) params.rowDimension = context.rowDimension;
      if (context.rowValue && !context.rowValues) params.rowValue = context.rowValue;

      const res = await getReportDrillDown(params);
      setData(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Drill-down error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isRevenue = reportType === 'revenue' || (reportType === 'matrix' && context?.dataSource === 'orders');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {context?.rowValues && context?.colValue
              ? `${context.rowValues.join(' / ')} / ${context.colValue}`
              : context?.rowValue && context?.colValue
                ? `${context.rowValue} / ${context.colValue}`
                : context?.stageName || context?.period || context?.groupLabel || 'Details'}
            <span className="drill-down-count"> ({pagination.total} records)</span>
          </h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-container"><div className="spinner"></div></div>
          ) : data.length === 0 ? (
            <p className="text-secondary">No records found.</p>
          ) : (
            <div className="report-table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>{isRevenue ? 'Order #' : 'Opportunity'}</th>
                    <th>Account</th>
                    {!isRevenue && <th>Stage</th>}
                    <th>Owner</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map(record => (
                    <tr key={record.id}>
                      <td>
                        <Link to={isRevenue ? `/orders/${record.id}` : `/opportunities/${record.id}`} onClick={onClose}>
                          {isRevenue ? record.order_number : record.name}
                        </Link>
                      </td>
                      <td>{record.account_name || '-'}</td>
                      {!isRevenue && <td>{record.stage_name || '-'}</td>}
                      <td>{record.owner_name || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(isRevenue ? record.total : record.amount)}</td>
                      <td>{isRevenue
                        ? record.order_date
                        : record.expected_close_date
                      }</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="drill-down-pagination">
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page <= 1}
                onClick={() => fetchData(pagination.page - 1)}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span>Page {pagination.page} of {pagination.totalPages}</span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchData(pagination.page + 1)}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
